/**
 * BulkEditRecurringModal.tsx
 *
 * "עריכה קבוצתית" — Bulk Edit for Recurring Action Lines.
 *
 * Rules:
 *  • Only fields explicitly filled by the user are applied; empty fields
 *    are left unchanged on every selected row.
 *  • "יום בחודש" replaces only the day portion of each row's action_date,
 *    keeping the original year and month intact (e.g., 2026-04-10 → 2026-04-15).
 *  • If the resulting day overflows the month (e.g., day=31 in April),
 *    Date automatically clamps to the last valid day.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, Pencil, Loader2, AlertTriangle } from 'lucide-react';
import type { AccountingCategory, RecurringActionLine } from '../../types/database';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface BulkEditFields {
  /** Free-text; empty string → skip */
  description: string;
  /** String representation; empty string → skip */
  amount: string;
  /** Category id; empty string → skip */
  accounting_category_id: string;
  /** 1-31; empty string → skip */
  dayOfMonth: string;
}

export interface BulkEditRecurringModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The full RecurringActionLine objects so we can read action_date for day-of-month replacement */
  selectedRows: RecurringActionLine[];
  accountingCategories: AccountingCategory[];
  isSaving: boolean;
  onSave: (fields: BulkEditFields) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const EMPTY: BulkEditFields = {
  description: '',
  amount: '',
  accounting_category_id: '',
  dayOfMonth: '',
};

function hasAtLeastOneField(f: BulkEditFields): boolean {
  return (
    f.description.trim() !== '' ||
    f.amount.trim() !== '' ||
    f.accounting_category_id !== '' ||
    f.dayOfMonth.trim() !== ''
  );
}

function validateDayOfMonth(v: string): string | null {
  if (v === '') return null;
  const n = parseInt(v, 10);
  if (isNaN(n) || n < 1 || n > 31) return 'יום חייב להיות בין 1 ל-31';
  return null;
}

function validateAmount(v: string): string | null {
  if (v === '') return null;
  const n = parseFloat(v);
  if (isNaN(n)) return 'סכום לא תקין';
  return null;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function BulkEditRecurringModal({
  isOpen,
  onClose,
  selectedRows,
  accountingCategories,
  isSaving,
  onSave,
}: BulkEditRecurringModalProps) {
  const [fields, setFields] = useState<BulkEditFields>(EMPTY);
  const [touched, setTouched] = useState<Partial<Record<keyof BulkEditFields, boolean>>>({});

  // Reset form whenever the modal opens
  useEffect(() => {
    if (isOpen) {
      setFields(EMPTY);
      setTouched({});
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving) onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, isSaving, onClose]);

  const set = useCallback(
    <K extends keyof BulkEditFields>(key: K, value: BulkEditFields[K]) => {
      setFields(prev => ({ ...prev, [key]: value }));
      setTouched(prev => ({ ...prev, [key]: true }));
    },
    [],
  );

  const dayError   = touched.dayOfMonth ? validateDayOfMonth(fields.dayOfMonth) : null;
  const amtError   = touched.amount     ? validateAmount(fields.amount)         : null;
  const canSave    = hasAtLeastOneField(fields) && !dayError && !amtError && !isSaving;
  const count      = selectedRows.length;

  if (!isOpen) return null;

  // ── Summary of which months appear in the selection (for day-of-month hint)
  const uniqueMonths = Array.from(
    new Set(selectedRows.map(r => r.action_date.slice(0, 7))),
  ).sort();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Mark all as touched for validation display
    setTouched({ description: true, amount: true, accounting_category_id: true, dayOfMonth: true });
    if (!canSave) return;
    onSave(fields);
  };

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      dir="rtl"
      onClick={e => { if (e.target === e.currentTarget && !isSaving) onClose(); }}
    >
      {/* Panel */}
      <div
        className="bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-md"
        style={{ maxHeight: '90vh' }}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-primary-50">
              <Pencil className="w-4 h-4 text-primary-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900 leading-tight">עריכה קבוצתית</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {count} פעולות נבחרות · מלא רק את השדות שברצונך לשנות
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSaving}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-40"
            aria-label="סגור"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ── Form ── */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-5 py-4 space-y-5">

            {/* Info banner */}
            <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2.5 text-sm text-blue-800">
              <AlertTriangle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>
                שדות ריקים <strong>לא ישתנו</strong>.
                רק שדות שמולאו יוחלו על כל הפעולות הנבחרות.
              </span>
            </div>

            {/* ── Description ── */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                תיאור
                <span className="mr-1 text-xs font-normal text-gray-400">(אופציונלי)</span>
              </label>
              <input
                type="text"
                value={fields.description}
                onChange={e => set('description', e.target.value)}
                placeholder="השאר ריק לדילוג"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900
                  placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* ── Amount ── */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                סכום
                <span className="mr-1 text-xs font-normal text-gray-400">(אופציונלי)</span>
              </label>
              <input
                type="number"
                step="0.01"
                value={fields.amount}
                onChange={e => set('amount', e.target.value)}
                placeholder="השאר ריק לדילוג"
                className={`w-full rounded-lg border px-3 py-2 text-sm text-gray-900
                  placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500
                  ${amtError ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'}`}
              />
              {amtError && (
                <p className="mt-1 text-xs text-red-600">{amtError}</p>
              )}
            </div>

            {/* ── Accounting Category ── */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                סיווג הנהלת חשבונות
                <span className="mr-1 text-xs font-normal text-gray-400">(אופציונלי)</span>
              </label>
              <select
                value={fields.accounting_category_id}
                onChange={e => set('accounting_category_id', e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">— השאר ריק לדילוג —</option>
                {accountingCategories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* ── Day of Month ── */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                יום בחודש
                <span className="mr-1 text-xs font-normal text-gray-400">(אופציונלי · 1–31)</span>
              </label>
              <input
                type="number"
                min={1}
                max={31}
                value={fields.dayOfMonth}
                onChange={e => set('dayOfMonth', e.target.value)}
                placeholder="השאר ריק לדילוג"
                className={`w-full rounded-lg border px-3 py-2 text-sm text-gray-900
                  placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500
                  ${dayError ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'}`}
              />
              {dayError && (
                <p className="mt-1 text-xs text-red-600">{dayError}</p>
              )}
              {/* Show affected months so the user can anticipate the result */}
              {fields.dayOfMonth.trim() !== '' && !dayError && uniqueMonths.length > 0 && (
                <p className="mt-1.5 text-xs text-gray-500 leading-relaxed">
                  יום {fields.dayOfMonth} יוחל על החודשים:&nbsp;
                  <span className="font-medium">{uniqueMonths.join(', ')}</span>
                </p>
              )}
            </div>

          </div>

          {/* ── Footer ── */}
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-gray-100 bg-gray-50/60 rounded-b-2xl">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700
                hover:bg-gray-50 transition-colors disabled:opacity-40"
            >
              ביטול
            </button>

            <button
              type="submit"
              disabled={!canSave}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-primary-600 text-white
                text-sm font-semibold hover:bg-primary-700 transition-colors
                disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none
                focus:ring-2 focus:ring-offset-1 focus:ring-primary-500"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  שומר…
                </>
              ) : (
                <>
                  <Pencil className="w-4 h-4" />
                  עדכן {count} פעולות
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
